function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { adBillingMode } from "../functions/tenant-features";
import { Container } from "./container";
import account from "../data/account";
import ServiceFactory from "../service";

/*
 * Provider Connections — the ONE canonical connections registry
 * (UX note §220 addendum 2, DS-confirmed). Lives on Manage Data → Connect;
 * section pages (Content, Social) render a contextual SLICE that points
 * here instead of growing their own connection managers.
 *
 * Sources:
 *   GET  /v1/content/connectors                 {items:[{id,type,provider,status,statusDetail,lastSync}]}
 *   POST /v1/content/connectors/connect         (admin) {provider,token,type,capabilities,config}
 *   DELETE /v1/content/connectors/{id}          (admin) → revoked
 *   GET  /v1/social/accounts                    {items:[{platform,handle,status,lastSync}]} — connect is
 *                                               team-side (§218 token-paste pilot), status only here.
 *
 * There is no import-trigger endpoint: the content sync worker picks a new
 * connector up on its next pass (Wed 06 UTC / on startup) — the connect
 * success copy sets that expectation instead of a dead "Import now" button.
 *
 * HEALTH: a connector row carries status ∈ connected|expired|error|revoked plus
 * a statusDetail message the sync worker writes on failure (e.g. a rejected
 * token). Anything other than 'connected' (bar an intentional 'revoked', which
 * reads as not-connected) shows amber "Reconnect needed" / "Needs attention",
 * surfaces statusDetail inline, is counted in the top attention banner, and
 * offers admins a Reconnect (re-paste token → same connect endpoint).
 */

function svc(api) {
  const Service = ServiceFactory.getType(api, "product"); // /v1 root
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
const isAdmin = () => ((account.get() || {}).groups || []).some(g => ["owner", "administrator", "root"].includes(String(g).toLowerCase()));

// The registry catalog. kind: content = token-paste connect via the content
// connectors API · social = status from the social rail (team-side connect) ·
// info = pointer to the feed cards above · link = another settings surface.
const PRETTY = {
  pinterest: "Pinterest",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  tiktok: "TikTok"
};
export const CONNECT_GROUPS = [{
  name: "Email",
  items: [{
    key: "constant_contact",
    label: "Constant Contact",
    kind: "content",
    type: "email",
    hint: "Campaign history imports into the Content Library; sends publish back out."
  }, {
    key: "mailchimp",
    label: "Mailchimp",
    kind: "content",
    type: "email",
    hint: "Campaign history imports into the Content Library; sends publish back out."
  }]
}, {
  name: "Social",
  items: [{
    key: "meta",
    label: "Meta (Facebook & Instagram)",
    kind: "social",
    platforms: ["facebook", "instagram"],
    hint: "Post stats, top posts, and publishing."
  }, {
    key: "google_business",
    label: "Google Business Profile",
    kind: "social",
    platforms: ["google_business", "gbp"],
    hint: "Reviews and local posts."
  }, {
    key: "pinterest",
    label: "Pinterest",
    kind: "social",
    oauth: true,
    platforms: ["pinterest"],
    hint: "Pins from the Content Hub to a board you pick."
  }, {
    key: "linkedin",
    label: "LinkedIn",
    kind: "social",
    oauth: true,
    platforms: ["linkedin"],
    hint: "Company-page posts from the Content Hub."
  }, {
    key: "youtube",
    label: "YouTube",
    kind: "social",
    oauth: true,
    platforms: ["youtube"],
    hint: "Narrated videos upload to your channel."
  }, {
    key: "tiktok",
    label: "TikTok",
    kind: "social",
    oauth: true,
    platforms: ["tiktok"],
    hint: "Reels post to your TikTok account."
  }]
}, {
  name: "Publishing",
  items: [{
    key: "wordpress",
    label: "WordPress",
    kind: "content",
    type: "cms",
    hint: "Publish approved content variants to your site."
  }, {
    key: "custom_api",
    label: "Custom API / webhook",
    kind: "content",
    type: "cms",
    custom: true,
    hint: "Any CMS, blog or site. Plunk POSTs each published piece as JSON to your endpoint (works with Zapier, Make, n8n, Webflow, Ghost, HubSpot, custom stacks)."
  }]
},

  // Hidden for now (DS 2026-10-01): PIM / DAM connectors are not ready to show.
  /* {
      name: "PIM / DAM", items: [
          { key: "salsify", label: "Salsify", kind: "content", type: "pim", hint: "Product content syndication." },
          { key: "brandfolder", label: "Brandfolder", kind: "content", type: "dam", hint: "Brand asset library." },
      ],
  } */
, {
  name: "Ads",
  items: [{
    key: "ads",
    label: "Ad providers (Google · Meta)",
    kind: "link",
    route: "/Campaigns/Settings",
    hint: "Account wiring for campaign launch lives in the Campaigns section."
  }]
}, {
  name: "Analytics",
  items: [{
    key: "tracker",
    label: "Plunk Tracker",
    kind: "info",
    hint: "Site tracking: status shows on the Website Tracking card above."
  }, {
    key: "ga4",
    label: "Google Analytics 4",
    kind: "info",
    hint: "One-time history import: see the Import History card above."
  }]
}];
const CHIP = {
  ok: {
    background: "#e7f6ec",
    color: "#1d7a3e"
  },
  warn: {
    background: "#fff3e0",
    color: "#b25e09"
  },
  off: {
    background: "var(--plk-surface-alt, #eef0f3)",
    color: "var(--plk-text-dim, #8a94a6)"
  }
};
function Chip({
  cls,
  children
}) {
  return /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 11,
      fontWeight: 700,
      borderRadius: 10,
      padding: "2px 8px",
      whiteSpace: "nowrap",
      ...CHIP[cls]
    }
  }, children);
}

// Resolve a catalog item's live status from the two rails.
export function connectionStatus(item, connectors, socialAccounts) {
  if (item.kind === "content") {
    const row = (connectors || []).find(c => String(c.provider || "").toLowerCase() === item.key && String(c.status || "").toLowerCase() !== "revoked");
    if (!row) return {
      cls: "off",
      label: "Not connected",
      row: null
    };
    const st = String(row.status || "").toLowerCase();
    if (st === "connected") return {
      cls: "ok",
      label: "Connected",
      row
    };
    // Any non-connected, non-revoked state is UNHEALTHY — never let 'expired'
    // or 'error' read as green. statusDetail carries the reason + fix hint.
    return {
      cls: "warn",
      label: st === "expired" ? "Reconnect needed" : "Needs attention",
      row
    };
  }
  if (item.kind === "social") {
    // A disconnected account stays in the list as "revoked"; it no longer counts as a connection.
    const rows = (socialAccounts || []).filter(a => (item.platforms || []).includes(String(a.platform || "").toLowerCase()) && String(a.status || "").toLowerCase() !== "revoked");
    if (!rows.length) return {
      cls: "off",
      label: "Not connected",
      row: null,
      rows
    };
    const broken = rows.find(a => String(a.status || "").toLowerCase() !== "connected");
    if (broken) return {
      cls: "warn",
      label: "Needs attention",
      row: broken,
      rows
    };
    return {
      cls: "ok",
      label: "Connected",
      row: rows[0],
      rows
    };
  }
  return {
    cls: "off",
    label: "",
    row: null
  };
}
export default class ProviderConnections extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "connect", async item => {
      const token = (this.state.token || "").trim();
      if (!token && !item.custom) {
        this.setState({
          note: "Paste the provider API key first."
        });
        return;
      }
      if (item.custom && !/^https:\/\//i.test((this.state.custom.url || "").trim())) {
        this.setState({
          note: "Enter the https endpoint URL first."
        });
        return;
      }
      this.setState({
        busy: true,
        note: null,
        testReply: null
      });
      try {
        const c = this.state.custom;
        const config = item.custom ? {
          url: c.url.trim(),
          method: c.method,
          auth_type: token ? c.auth_type : "none",
          header_name: c.header_name,
          sign: !!c.sign,
          id_path: c.id_path || "id",
          url_path: c.url_path || "url",
          template: (c.template || "").trim() || undefined
        } : undefined;
        // the connect endpoint requires a token; a Custom API without auth stores a placeholder secret it never sends
        await svc("/content/connectors/connect").postAsync({
          provider: item.key,
          token: token || (item.custom ? "none" : token),
          type: item.type || "email",
          capabilities: item.custom ? "write" : undefined,
          config: config ? JSON.stringify(config) : undefined
        });
        if (!this._alive) return;
        this.setState({
          busy: false,
          connecting: null,
          token: "",
          note: item.custom ? "Custom API connected: use Send test to check your endpoint, then Publish → Custom API on any variant." : `${item.label} connected: the history import runs on the next content sync (within a day); imported campaigns land in the Content Library.`
        });
        this.load();
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          note: e.message && e.message.length < 160 ? e.message : "Connect failed: check the key and try again."
        });
      }
    });
    _defineProperty(this, "testCustom", async () => {
      this.setState({
        busy: true,
        testReply: null
      });
      try {
        const r = await svc("/content/connectors/test").postAsync({
          provider: "custom_api"
        });
        this.setState({
          busy: false,
          testReply: r
        });
      } catch (e) {
        this.setState({
          busy: false,
          testReply: {
            ok: false,
            status: 0,
            reply: e && e.message || "Request failed"
          }
        });
      }
    });
    _defineProperty(this, "setCustom", patch => this.setState({
      custom: {
        ...this.state.custom,
        ...patch
      }
    }));
    // Meta OAuth (P2): "Connect with Facebook" — the real login + grant flow.
    // Ask the backend for the OAuth dialog URL (it holds the app id/secret), then
    // send the top window to Meta. Meta returns to /OAuth/Meta which finishes it.
    _defineProperty(this, "startMetaOAuth", async rerequest => {
      this.setState({
        busy: true,
        note: null
      });
      try {
        const redirectUri = window.location.origin + "/OAuth/Meta";
        // Reconnect re-requests: Meta skips a previously declined permission unless asked again.
        const r = await svc("/social/oauth/meta/start?redirectUri=" + encodeURIComponent(redirectUri) + (rerequest ? "&rerequest=true" : "")).getAsync();
        if (r && r.url) {
          window.location.href = r.url;
          return;
        }
        this.setState({
          busy: false,
          note: "Meta connect is not configured yet: email support@plunk.io and we will link it."
        });
      } catch (e) {
        this.setState({
          busy: false,
          note: e && e.message && e.message.length < 160 ? e.message : "Could not start the Meta connection."
        });
      }
    });
    // Ship connectors (2026-09-27): Pinterest / LinkedIn / YouTube / TikTok share one OAuth shape. The backend
    // holds the client id/secret; the top window goes to the provider and returns to /OAuth/<provider>.
    _defineProperty(this, "startOAuth", async provider => {
      this.setState({
        busy: true,
        note: null
      });
      try {
        const redirectUri = window.location.origin + "/OAuth/" + provider;
        const r = await svc(`/social/oauth/${provider}/start?redirectUri=` + encodeURIComponent(redirectUri)).getAsync();
        if (r && r.url) {
          window.location.href = r.url;
          return;
        }
        this.setState({
          busy: false,
          note: `${PRETTY[provider] || provider} connect is not configured yet: email support@plunk.io and we will link it.`
        });
      } catch (e) {
        this.setState({
          busy: false,
          note: e && e.message && e.message.length < 160 ? e.message : `Could not start the ${PRETTY[provider] || provider} connection.`
        });
      }
    });
    _defineProperty(this, "disconnect", async row => {
      this.setState({
        busy: true,
        note: null
      });
      try {
        await svc(`/content/connectors/${String(row.id)}`).deleteAsync();
        if (!this._alive) return;
        this.setState({
          busy: false,
          confirmDisconnect: null,
          note: "Disconnected: imports stop; already-imported content stays in the Library."
        });
        this.load();
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          note: "Disconnect failed: try again."
        });
      }
    });
    /** Social disconnect: every live account behind the row (Meta = the Page and its Instagram account). The API revokes
     *  the grant with the network where it can and deletes the stored token. */
    _defineProperty(this, "disconnectSocial", async (item, rows) => {
      this.setState({
        busy: true,
        note: null
      });
      try {
        for (const a of rows || []) await svc(`/social/accounts/${String(a.id)}`).deleteAsync();
        if (!this._alive) return;
        this.setState({
          busy: false,
          confirmDisconnect: null,
          note: `Disconnected ${PRETTY[item.key] || item.label}: Plunk no longer has access, and what you already published stays where it is.`
        });
        this.load();
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          note: "Disconnect failed: try again."
        });
      }
    });
    this.state = {
      connectors: null,
      social: null,
      connecting: null,
      token: "",
      busy: false,
      note: null,
      confirmDisconnect: null,
      custom: {
        url: "",
        method: "POST",
        auth_type: "bearer",
        header_name: "X-Api-Key",
        sign: false,
        id_path: "id",
        url_path: "url",
        template: ""
      },
      testReply: null
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load() {
    const [conn, social] = await Promise.all([safe(svc("/content/connectors").getAsync(), {
      items: []
    }), safe(svc("/social/accounts").getAsync(), {
      items: []
    })]);
    if (this._alive) this.setState({
      connectors: conn.items || [],
      social: social.items || []
    });
  }
  render() {
    let selfService = false;
    try {
      selfService = adBillingMode() !== "managed";
    } catch (e) {
      selfService = false;
    }
    const {
      connectors,
      social,
      connecting,
      token,
      busy,
      note,
      confirmDisconnect
    } = this.state;
    const admin = isAdmin();
    if (connectors === null) return null;
    // Connections needing attention (expired / error) — surfaced up top so a
    // broken sync is caught without scrolling the whole registry.
    const attention = CONNECT_GROUPS.flatMap(g => g.items).map(it => ({
      it,
      st: connectionStatus(it, connectors, social)
    })).filter(x => x.st.cls === "warn");
    return /*#__PURE__*/React.createElement(Container, {
      title: "Provider connections",
      css: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 10px",
        maxWidth: "72ch"
      }
    }, "Every external account the suite reads from or publishes to, in one place.", admin ? " Connecting takes the provider API key: each card says where to find it, and support@plunk.io can help." : " Connections are managed by workspace owners."), attention.length > 0 && /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 10px",
        padding: "8px 12px",
        borderRadius: 8,
        background: "#fff3e0",
        color: "#8a4b09",
        fontSize: 13
      }
    }, /*#__PURE__*/React.createElement("b", null, attention.length, " connection", attention.length > 1 ? "s" : "", " need", attention.length > 1 ? "" : "s", " attention:"), " ", attention.map(x => x.it.label).join(", "), admin ? ": reconnect below." : ": a workspace owner can reconnect."), note && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 10px",
        borderLeft: "3px solid var(--plk-accent, #ff6358)",
        paddingLeft: 10
      }
    }, note), CONNECT_GROUPS.map(group => /*#__PURE__*/React.createElement("div", {
      key: group.name,
      style: {
        marginBottom: 12
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        fontSize: 11,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "var(--plk-muted, #8a94a6)",
        margin: "0 0 6px"
      }
    }, group.name), group.items.map(item => {
      const st = connectionStatus(item, connectors, social);
      const open = connecting === item.key;
      return /*#__PURE__*/React.createElement("div", {
        key: item.key,
        style: {
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          padding: "8px 0",
          borderBottom: "1px solid var(--plk-border, #e4e7ec)"
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          minWidth: 230
        }
      }, /*#__PURE__*/React.createElement("b", {
        style: {
          fontSize: 13.5
        }
      }, item.label), /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, item.hint)), item.kind === "link" ? /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.props.history && this.props.history.push(item.route)
      }, "Open settings") : item.kind === "info" ? /*#__PURE__*/React.createElement(Chip, {
        cls: "off"
      }, "See cards above") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Chip, {
        cls: st.cls
      }, st.label), st.row && st.row.lastSync && /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, "last sync ", String(st.row.lastSync).slice(0, 10)), st.cls === "warn" && st.row && st.row.statusDetail && /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0,
          flexBasis: "100%",
          color: "#8a4b09"
        }
      }, st.row.statusDetail), item.kind === "social" ?
      // Meta supports client-side OAuth connect; other social
      // platforms remain team-side for now.
      item.key === "meta" && admin ? /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        disabled: busy,
        onClick: () => this.startMetaOAuth(st.cls !== "off")
      }, st.cls === "off" ? "Connect with Facebook" : "Reconnect with Facebook") : item.oauth && admin ? /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        disabled: busy,
        onClick: () => this.startOAuth(item.key)
      }, st.cls === "off" ? `Connect with ${PRETTY[item.key]}` : `Reconnect with ${PRETTY[item.key]}`) : item.key === "meta" || item.oauth ? st.cls === "off" && /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, "Connections are managed by workspace owners.") : st.cls === "off" && /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, selfService ? "Google Business Profile linking is done by support for now: email support@plunk.io with your Business Profile name." : "Connected by the Plunk team: contact your rep.") : admin && (st.cls !== "ok" ? /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.setState({
          connecting: open ? null : item.key,
          token: "",
          note: null
        })
      }, open ? "Cancel" : st.cls === "warn" ? "Reconnect" : "Connect") : confirmDisconnect === item.key ? /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          gap: 6,
          alignItems: "center"
        }
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12.5
        }
      }, "Imports stop; imported content stays."), /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        disabled: busy,
        onClick: () => this.disconnect(st.row)
      }, "Confirm"), /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.setState({
          confirmDisconnect: null
        })
      }, "Keep")) : /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.setState({
          confirmDisconnect: item.key,
          note: null
        })
      }, "Disconnect")), item.kind === "social" && admin && st.cls !== "off" && (st.rows || []).length > 0 && (confirmDisconnect === item.key ? /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          gap: 6,
          alignItems: "center"
        }
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          fontSize: 12.5
        }
      }, "Plunk loses access; published posts stay."), /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        disabled: busy,
        onClick: () => this.disconnectSocial(item, st.rows)
      }, "Confirm"), /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        onClick: () => this.setState({
          confirmDisconnect: null
        })
      }, "Keep")) : /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        disabled: busy,
        onClick: () => this.setState({
          confirmDisconnect: item.key,
          note: null
        })
      }, "Disconnect"))), item.custom && admin && !open && st.cls === "ok" && /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap"
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle",
        disabled: busy,
        onClick: this.testCustom
      }, busy ? "Sending…" : "Send test"), this.state.testReply && /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0,
          color: this.state.testReply.ok ? "var(--plk-success, #217a4b)" : "var(--plk-danger, #b3383f)"
        }
      }, this.state.testReply.ok ? `OK · HTTP ${this.state.testReply.status}` : `Failed · HTTP ${this.state.testReply.status}`, " ", String(this.state.testReply.reply || "").slice(0, 160))), open && item.custom && admin && (() => {
        const c = this.state.custom;
        const inp = {
          padding: "6px 9px",
          fontSize: 13,
          border: "1px solid var(--plk-border, #d5dbe3)",
          borderRadius: 6
        };
        return /*#__PURE__*/React.createElement("div", {
          style: {
            flexBasis: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 8,
            padding: "6px 0 2px"
          }
        }, /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12,
            gridColumn: "1 / -1"
          }
        }, "Endpoint URL", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("input", {
          value: c.url,
          placeholder: "https://your-site.com/api/plunk-publish",
          onChange: e => this.setCustom({
            url: e.target.value
          }),
          style: {
            ...inp,
            width: "100%"
          }
        })), /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12
          }
        }, "Method", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("select", {
          value: c.method,
          onChange: e => this.setCustom({
            method: e.target.value
          }),
          style: inp
        }, /*#__PURE__*/React.createElement("option", null, "POST"), /*#__PURE__*/React.createElement("option", null, "PUT"))), /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12
          }
        }, "Auth", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("select", {
          value: c.auth_type,
          onChange: e => this.setCustom({
            auth_type: e.target.value
          }),
          style: inp
        }, /*#__PURE__*/React.createElement("option", {
          value: "bearer"
        }, "Bearer token"), /*#__PURE__*/React.createElement("option", {
          value: "header"
        }, "Custom header"), /*#__PURE__*/React.createElement("option", {
          value: "basic"
        }, "Basic (user:password)"), /*#__PURE__*/React.createElement("option", {
          value: "none"
        }, "None"))), c.auth_type === "header" && /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12
          }
        }, "Header name", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("input", {
          value: c.header_name,
          onChange: e => this.setCustom({
            header_name: e.target.value
          }),
          style: inp
        })), c.auth_type !== "none" && /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12
          }
        }, "Secret", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("input", {
          type: "password",
          value: token,
          placeholder: c.auth_type === "basic" ? "user:password" : "token",
          onChange: e => this.setState({
            token: e.target.value
          }),
          style: {
            ...inp,
            fontFamily: "monospace"
          }
        })), /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12
          }
        }, "Reply id path", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("input", {
          value: c.id_path,
          placeholder: "id",
          onChange: e => this.setCustom({
            id_path: e.target.value
          }),
          style: inp
        })), /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12
          }
        }, "Reply URL path", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("input", {
          value: c.url_path,
          placeholder: "url",
          onChange: e => this.setCustom({
            url_path: e.target.value
          }),
          style: inp
        })), /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12,
            display: "flex",
            gap: 6,
            alignItems: "center"
          }
        }, /*#__PURE__*/React.createElement("input", {
          type: "checkbox",
          checked: !!c.sign,
          onChange: e => this.setCustom({
            sign: e.target.checked
          })
        }), " Sign payloads (X-Plunk-Signature, HMAC-SHA256 with the secret)"), /*#__PURE__*/React.createElement("label", {
          style: {
            fontSize: 12,
            gridColumn: "1 / -1"
          }
        }, "Payload template ", /*#__PURE__*/React.createElement("span", {
          className: "territory-hint",
          style: {
            margin: 0
          }
        }, "\xB7 optional JSON with ", "{{title}} {{body}} {{html}} {{excerpt}} {{channel}} {{kind}} {{images}} {{image}} {{videoUrl}} {{utm}} {{tags}} {{containerId}} {{variantId}} {{storyId}} {{publishedAt}}", "; blank = the default payload with all of them"), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("textarea", {
          value: c.template,
          rows: 3,
          placeholder: "{\"title\": {{title}}, \"content\": {{html}}, \"status\": \"draft\"}",
          onChange: e => this.setCustom({
            template: e.target.value
          }),
          style: {
            ...inp,
            width: "100%",
            fontFamily: "monospace",
            fontSize: 12
          }
        })), /*#__PURE__*/React.createElement("div", {
          style: {
            gridColumn: "1 / -1",
            display: "flex",
            gap: 8
          }
        }, /*#__PURE__*/React.createElement("button", {
          className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
          disabled: busy,
          onClick: () => this.connect(item)
        }, busy ? "Connecting…" : "Save connection")));
      })(), open && item.kind === "content" && !item.custom && admin && /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap"
        }
      }, /*#__PURE__*/React.createElement("input", {
        type: "password",
        value: token,
        placeholder: `${item.label} API key`,
        onChange: e => this.setState({
          token: e.target.value
        }),
        style: {
          width: 260,
          padding: "6px 9px",
          fontSize: 13,
          fontFamily: "monospace",
          border: "1px solid var(--plk-border, #d5dbe3)",
          borderRadius: 6
        }
      }), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        disabled: busy,
        onClick: () => this.connect(item)
      }, busy ? "Connecting…" : "Connect")));
    }))));
  }
}